<script setup>
import { ref, computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import ContactsForm from 'dashboard/components-next/Contacts/ContactsForm/ContactsForm.vue';

const emit = defineEmits(['create']);

const { t } = useI18n();

const dialogRef = ref(null);
const contactsFormRef = ref(null);
const contact = ref(null);

const uiFlags = useMapGetter('contacts/getUIFlags');
const isCreatingContact = computed(() => uiFlags.value.isCreating);

const createNewContact = contactItem => {
  contact.value = contactItem;
};

const handleDialogConfirm = async () => {
  if (!contact.value) return;
  emit('create', contact.value);
};

const onSuccess = () => {
  contactsFormRef.value?.resetForm();
  dialogRef.value.close();
};

const closeDialog = () => {
  dialogRef.value.close();
};

defineExpose({ dialogRef, contactsFormRef, onSuccess });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="3xl"
    overflow-y-auto
    panel-class="max-md:gap-4 max-md:p-4 max-md:pb-3"
    @confirm="handleDialogConfirm"
  >
    <div class="hidden items-center justify-between max-md:flex">
      <div class="flex items-center gap-3">
        <span
          class="flex size-10 items-center justify-center rounded-xl bg-n-brand/10 text-n-blue-11"
        >
          <Icon icon="i-lucide-user-round-plus" class="size-5" />
        </span>
        <h3 class="mb-0 text-base font-semibold text-n-slate-12">
          {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.ADD_CONTACT') }}
        </h3>
      </div>
      <Button
        type="button"
        icon="i-lucide-x"
        variant="ghost"
        color="slate"
        size="sm"
        :aria-label="t('DIALOG.BUTTONS.CANCEL')"
        @click="closeDialog"
      />
    </div>
    <ContactsForm
      ref="contactsFormRef"
      is-new-contact
      @update="createNewContact"
    />
    <template #footer>
      <div
        class="flex w-full items-center justify-between gap-3 max-md:sticky max-md:bottom-0 max-md:z-10 max-md:-mx-4 max-md:-mb-3 max-md:w-auto max-md:border-t max-md:border-n-weak max-md:bg-n-alpha-3 max-md:px-4 max-md:pb-3 max-md:pt-3"
      >
        <Button
          :label="t('DIALOG.BUTTONS.CANCEL')"
          variant="link"
          type="reset"
          class="h-10 hover:!no-underline hover:text-n-brand max-md:w-full max-md:rounded-lg max-md:bg-n-alpha-2"
          @click="closeDialog"
        />
        <Button
          type="submit"
          :label="
            t('CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.SAVE_CONTACT')
          "
          color="blue"
          class="max-md:w-full"
          :disabled="contactsFormRef?.isFormInvalid"
          :is-loading="isCreatingContact"
        />
      </div>
    </template>
  </Dialog>
</template>
